/**
 * Die einzige Stelle, an der Dokumentsummen gerechnet werden.
 *
 * Client und Server binden dieselbe Datei ein. Der Client rechnet für die
 * Anzeige, der Server rechnet beim Abschluss verbindlich nach. Weichen die
 * Ergebnisse ab, ist das ein Fehler und wird gemeldet – nicht stillschweigend
 * übernommen.
 *
 * Übernommen aus AV Parkett Office, umgestellt von Franken auf Rappen.
 */

import { lineTotal, percentOf, roundTo5Rp, type Rappen } from './money.js'

export interface CalcLine {
  quantity: number
  unitPriceRp: Rappen
  /** MWST-Satz in Prozent, z.B. 8.1 */
  vatRate: number
  /** Positionsrabatt in Prozent, 0 wenn keiner */
  discountPercent?: number
}

export interface CalcResult {
  lines: Array<{ netRp: Rappen; vatRp: Rappen; grossRp: Rappen }>
  subtotalRp: Rappen
  /** MWST nach Satz getrennt – die Rechnung muss das ausweisen können */
  vatBySatz: Array<{ rate: number; baseRp: Rappen; vatRp: Rappen }>
  vatTotalRp: Rappen
  totalRp: Rappen
}

export function calcDocument(lines: CalcLine[]): CalcResult {
  const out: CalcResult['lines'] = []
  const byRate = new Map<number, { baseRp: Rappen; vatRp: Rappen }>()
  let subtotalRp = 0

  for (const l of lines) {
    const raw = lineTotal(l.quantity, l.unitPriceRp)
    const discount = l.discountPercent ? percentOf(raw, l.discountPercent) : 0
    const netRp = raw - discount
    const vatRp = percentOf(netRp, l.vatRate)

    out.push({ netRp, vatRp, grossRp: netRp + vatRp })
    subtotalRp += netRp

    const bucket = byRate.get(l.vatRate) ?? { baseRp: 0, vatRp: 0 }
    bucket.baseRp += netRp
    byRate.set(l.vatRate, bucket)
  }

  // MWST wird je Satz auf der Summe gerechnet, nicht als Summe der Positions-MWST.
  // Sonst weicht das Total um ein paar Rappen von dem ab, was der Treuhänder nachrechnet.
  const vatBySatz = [...byRate.entries()]
    .map(([rate, b]) => ({ rate, baseRp: b.baseRp, vatRp: percentOf(b.baseRp, rate) }))
    .sort((a, b) => a.rate - b.rate)

  const vatTotalRp = vatBySatz.reduce((s, v) => s + v.vatRp, 0)

  return { lines: out, subtotalRp, vatBySatz, vatTotalRp, totalRp: subtotalRp + vatTotalRp }
}

/**
 * Dieselbe Rechnung, aber mit dem Gesamtrabatt des Belegs.
 *
 * ────────────────────────────────────────────────────────────────────────────
 * Warum es diese Funktion gibt
 * ────────────────────────────────────────────────────────────────────────────
 *
 * `calcDocument` oben kennt nur den Rabatt AUF DER POSITION. Der Rabatt auf
 * den GANZEN Beleg – `documents.discount_percent` – wurde an einer einzigen
 * Stelle im Server gerechnet (services/summen.ts) und an drei anderen nicht:
 * beim Abschluss, bei den Daten fürs PDF und beim Lesen eines Belegs.
 *
 * Gemessen an einer Rechnung über 1'000.00 mit 15 % Gesamtrabatt: gespeichert
 * wurde `discount_amount_rp = 15000`, auf dem Papier stand 1'081.00, und
 * derselbe Betrag stand im QR-Zahlteil. Der Kunde hätte 150.00 zu viel
 * bezahlt, und weder Papier noch Zahlteil hätten widersprochen – sie waren ja
 * einig.
 *
 * Die Rechnung gehört deshalb hierher, neben die andere, und wird von allen
 * Stellen aufgerufen. Eine zweite Kopie wäre der nächste Betrag, der
 * auseinanderläuft.
 *
 * ────────────────────────────────────────────────────────────────────────────
 * Zur Mehrwertsteuer
 * ────────────────────────────────────────────────────────────────────────────
 *
 * Der Rabatt wirkt auf das Netto, BEVOR die Steuer gerechnet wird – sonst
 * lieferte der Betrieb Steuer auf Geld ab, das er nie bekommen hat.
 *
 * Bei mehreren Sätzen auf einem Beleg wird die Bemessungsgrundlage jedes
 * Satzes im selben Verhältnis gekürzt und die Steuer daraus NEU gerechnet.
 * Die frühere Fassung kürzte stattdessen den Steuerbetrag direkt; das ergibt
 * fast immer dasselbe und gelegentlich einen Rappen daneben – und dann
 * stimmt die Aufstellung auf dem Beleg nicht mehr mit ihrer eigenen Summe
 * überein. Der Treuhänder rechnet aus der Grundlage, also rechnen wir auch
 * aus der Grundlage.
 *
 * Der letzte Satz bekommt den Rundungsrest, damit die Summe der Grundlagen
 * exakt das Zwischentotal nach Rabatt ergibt.
 */
export interface BelegSummen extends CalcResult {
  /** Netto der Positionen, nach Positionsrabatt, VOR dem Gesamtrabatt. */
  zwischentotalRp: Rappen
  /** Gesamtrabatt in Prozent. 0, wenn keiner gesetzt ist. */
  rabattProzent: number
  /** Der Betrag des Gesamtrabatts. 0, wenn keiner gesetzt ist. */
  rabattRp: Rappen
}

export function calcBeleg(lines: CalcLine[], rabattProzent = 0): BelegSummen {
  const roh = calcDocument(lines)
  const p = Number.isFinite(rabattProzent) ? rabattProzent : 0

  if (!(p > 0) || roh.subtotalRp === 0) {
    return { ...roh, zwischentotalRp: roh.subtotalRp, rabattProzent: p > 0 ? p : 0, rabattRp: 0 }
  }

  const zwischentotalRp = roh.subtotalRp
  const rabattRp = Math.round((zwischentotalRp * p) / 100)
  const subtotalRp = zwischentotalRp - rabattRp

  // Grundlagen im Verhältnis kürzen; der letzte Satz trägt den Rest.
  const vatBySatz = roh.vatBySatz.map((v) => ({ ...v }))
  let verteilt = 0
  for (let i = 0; i < vatBySatz.length; i++) {
    const letzter = i === vatBySatz.length - 1
    const baseRp = letzter
      ? subtotalRp - verteilt
      : Math.round((vatBySatz[i].baseRp * subtotalRp) / zwischentotalRp)
    verteilt += baseRp
    vatBySatz[i].baseRp = baseRp
    vatBySatz[i].vatRp = percentOf(baseRp, vatBySatz[i].rate)
  }
  const vatTotalRp = vatBySatz.reduce((s, v) => s + v.vatRp, 0)

  return {
    lines: roh.lines,
    zwischentotalRp,
    rabattProzent: p,
    rabattRp,
    subtotalRp,
    vatBySatz,
    vatTotalRp,
    totalRp: subtotalRp + vatTotalRp
  }
}

/* ═══════════════════════════════════════════════════════════════════════════
   Die Schweizer Rundung auf fünf Rappen
   ═══════════════════════════════════════════════════════════════════════════ */

/**
 * Warum die Rundung GANZ zum Schluss kommt – und nur einmal.
 *
 * Es gibt in der Schweiz keine Ein- und Zwei-Rappen-Stücke mehr. Ein Endtotal
 * von CHF 100.03 lässt sich nicht bezahlen, und eine Rechnung, die ihn
 * verlangt, erzeugt beim Kunden eine Differenz von zwei Rappen, die danach
 * durch die ganze Buchhaltung läuft.
 *
 * Gerundet wird deshalb GENAU EINMAL, auf dem Endtotal nach Rabatt und
 * Mehrwertsteuer. Nicht je Position – dann summieren sich bis zu zweieinhalb
 * Rappen je Zeile zu einem Betrag, der weder zur Positionsliste noch zur
 * MWST-Aufstellung passt. Nicht auf dem Zwischentotal – dann stimmt die
 * Mehrwertsteuer nicht mehr zu ihrer eigenen Grundlage.
 *
 * Die Folge ist eine Zeile mehr auf dem Beleg: Zwischentotal, MWST, Rundung,
 * Total. Das ist kein Schönheitsfehler, sondern die Pflicht – der Treuhänder
 * muss sehen, woher die Differenz kommt, und die MWST-Grundlagen bleiben
 * unangetastet.
 *
 * Kaufmännisch heisst: 2.5 Rappen gehen nach oben. `Math.round(rp / 5) * 5`
 * bleibt dabei die ganze Zeit in Ganzzahlen; ein Zwischenschritt über Franken
 * würde genau den Fliesskommafehler einführen, den `money.ts` verbietet.
 */
export interface GerundeteSummen extends BelegSummen {
  /** Das Endtotal, BEVOR auf fünf Rappen gerundet wurde. */
  ungerundetRp: Rappen
  /** Die Differenz der Rundung. Positiv heisst aufgerundet, negativ abgerundet. */
  rundungRp: Rappen
}

/**
 * Die Währungen, für die gerundet wird.
 *
 * Heute führt Raporto ausschliesslich Franken – es gibt keine Währungsspalte.
 * Der Parameter steht trotzdem da, und zwar aus einem Grund: Die
 * Fünf-Rappen-Regel ist eine SCHWEIZER Besonderheit. Käme morgen der Euro
 * dazu und die Rundung liefe stillschweigend mit, wären alle Euro-Rechnungen
 * falsch. Lieber ein Parameter, der heute immer denselben Wert hat, als eine
 * Regel, die später ungeprüft erbt.
 */
export type Waehrung = 'CHF' | (string & {})

/** Nur Franken werden auf fünf Rappen gerundet. */
export function rundetAufFuenfRappen(waehrung: Waehrung): boolean {
  return waehrung === 'CHF'
}

/**
 * Das Endtotal auf fünf Rappen runden.
 *
 * Für Belege, die noch NICHT versendet sind. Ein versendeter Beleg wird nie
 * nachgerechnet – für den gilt `mitRundung` mit der gespeicherten Differenz.
 */
export function rundeEndtotal(summen: BelegSummen, waehrung: Waehrung = 'CHF'): GerundeteSummen {
  if (!rundetAufFuenfRappen(waehrung)) {
    return { ...summen, ungerundetRp: summen.totalRp, rundungRp: 0 }
  }
  const gerundet = roundTo5Rp(summen.totalRp)
  return {
    ...summen,
    ungerundetRp: summen.totalRp,
    rundungRp: gerundet - summen.totalRp,
    totalRp: gerundet
  }
}

/**
 * Eine GESPEICHERTE Rundung wieder anwenden.
 *
 * Das ist die Gegenrichtung und der Grund, warum die Differenz überhaupt in
 * der Datenbank steht: Wird das PDF eines bereits abgeschlossenen Belegs neu
 * gedruckt, darf sich der Betrag nicht ändern – auch dann nicht, wenn die
 * Rundungsregel inzwischen eine andere wäre. Gerechnet wird der Rohbetrag,
 * aufgeschlagen wird die Differenz, die damals galt.
 *
 * Für jeden Altbeleg steht dort 0. Er bekommt damit exakt seine alte Summe,
 * und die Gleichheitsprüfung vor dem Druck bleibt grün.
 */
export function mitRundung(summen: BelegSummen, rundungRp: Rappen): GerundeteSummen {
  const d = Number.isFinite(rundungRp) ? Math.round(rundungRp) : 0
  return {
    ...summen,
    ungerundetRp: summen.totalRp,
    rundungRp: d,
    totalRp: summen.totalRp + d
  }
}

/** Zahlungsstatus wird abgeleitet, nie gespeichert. */
export type PaymentStatus = 'offen' | 'teilbezahlt' | 'bezahlt' | 'ueberbezahlt'

export function paymentStatus(totalRp: Rappen, paidRp: Rappen): PaymentStatus {
  if (paidRp <= 0) return 'offen'
  if (paidRp < totalRp) return 'teilbezahlt'
  if (paidRp === totalRp) return 'bezahlt'
  return 'ueberbezahlt'
}

/** Fälligkeit aus Rechnungsdatum und Zahlungsfrist. */
export function dueDate(issueDate: Date, termDays: number): Date {
  const d = new Date(issueDate)
  d.setUTCDate(d.getUTCDate() + termDays)
  return d
}
